/**
 * sb-chrome.js — SoundBird header chrome behaviour
 * ---------------------------------------------------------------------------
 * One state machine for the whole chrome. Exactly one overlay is open at a
 * time — `none | mega | country | search | cart | menu` — written to
 * `data-overlay` on the `.sb-c` root, which every panel's CSS reads. Keeping
 * the state in one attribute is what makes "opening X closes Y" free rather
 * than a web of pairwise handlers.
 *
 * Vanilla, no dependencies (theme rule). Pointer events throughout, never
 * mouse/touch pairs.
 */
(() => {
  const root = document.querySelector('[data-sb-chrome]');
  if (!root) return;

  /* ── Input modality ─────────────────────────────────────────────────
     Marks <html> while the visitor is driving with a pointer, so the focus
     ring can be suppressed for mouse and touch and kept for the keyboard.

     :focus-visible alone is not enough here. This chrome RETURNS FOCUS by
     script — closing a panel puts focus back on the trigger, drilling a level
     puts it on the first row — and a scripted focus can still paint a ring even
     though the gesture behind it was a click. That is where the orange rings
     appearing "when you open pages" come from: not from hovering, but from the
     focus this code moves on your behalf a moment later.

     Deliberately NOT a blanket `outline: none`. Remove the ring outright and
     the site becomes unusable by keyboard, which is both a WCAG 2.4.7 failure
     and something the handoff asks for explicitly. This keeps it for the people
     who need it and hides it from the people who do not. */
  const setPointer = () => document.documentElement.setAttribute('data-pointer', '');
  const clearPointer = (e) => {
    /* Only navigation keys count — typing into the search field should not
       start painting rings around everything. */
    if (['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Escape'].includes(e.key)) {
      document.documentElement.removeAttribute('data-pointer');
    }
  };
  setPointer();
  document.addEventListener('pointerdown', setPointer, { capture: true, passive: true });
  document.addEventListener('keydown', clearPointer, { capture: true });

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  /* 989px, matching sb-chrome-styles — the chrome's own breakpoint, chosen so
     the desktop layout only appears where the 980px mega menu actually fits.
     Deliberately not the theme's 749px; see the note at the top of that file. */
  const MOBILE_Q = matchMedia('(max-width: 989px)');
  const mobile = () => MOBILE_Q.matches;

  /* ── Overlay state ──────────────────────────────────────────────────
     `lastTrigger` is what focus returns to on close. It is captured on open
     rather than read on close, because by then the trigger may have been
     re-rendered by a cart update and the reference would be stale. */
  let overlay = 'none';
  let lastTrigger = null;
  let scrollY = 0;

  const TRIGGERS = {
    mega: '[data-sb-mega-trigger]',
    country: '[data-sb-country-trigger]',
    search: '[data-sb-search-trigger]',
    cart: '[data-sb-cart-trigger]',
    menu: '[data-sb-menu-trigger]',
  };

  /* Hover-opened overlays must NOT lock the page: freezing the document
     because a pointer crossed the nav is hostile, and the mega menu closes on
     mouseleave anyway. Click-opened surfaces do lock. */
  const LOCKS = new Set(['country', 'search', 'cart', 'menu']);

  /**
   * THERE ARE TWO SCROLLERS, and which one is live depends on the breakpoint.
   * Horizon's base.css does this at >= 990px:
   *
   *   html:has(.page-wrapper), html:has(.page-wrapper) body {
   *     height: 100dvh; overflow: hidden;
   *   }
   *
   * so on desktop the DOCUMENT does not scroll at all — `.page-wrapper` does,
   * and window.scrollY is permanently 0. Below 990px the document scrolls
   * normally. A lock that only knows about one of them is half a lock.
   *
   * Core already ships the right lever for the element scroller:
   * `html[scroll-lock]` freezes html and `.page-wrapper` together, and an
   * element scroller keeps its own scrollTop while frozen, so nothing needs to
   * be recorded or restored.
   *
   * The document scroller still needs the position:fixed treatment, because
   * iOS will not hold a page with `overflow:hidden` alone — and the naive form
   * of that jumps the page to the top, so the offset is recorded and re-applied
   * as a negative `top`. That half is scoped to mobile: applying it on desktop
   * makes body fixed underneath core's `height:100dvh` layout, which happens to
   * stop the scroll today only because window.scrollY is always 0 there.
   */
  function lockScroll() {
    if (document.body.hasAttribute('data-sb-locked')) return;

    /* READ THE OFFSET FIRST. `data-sb-locked` makes the body position:fixed,
       which takes it out of flow and collapses the document to viewport
       height — and the scroll position is gone the moment that lands. Reading
       window.scrollY afterwards forces the pending style to flush and then
       returns 0, so the page was pinned at top:0 and every menu, search or
       cart open threw the visitor back to the top of the page and never
       brought them back (owner 2026-08-04). Measured before the fix: scrolled
       to 900, opened the menu, scrollY 0, body top 0px. */
    const y = window.scrollY;

    document.body.setAttribute('data-sb-locked', '');
    document.documentElement.setAttribute('scroll-lock', '');
    if (!mobile()) return;
    scrollY = y;
    document.body.style.top = `-${scrollY}px`;
  }

  function unlockScroll() {
    if (!document.body.hasAttribute('data-sb-locked')) return;
    document.body.removeAttribute('data-sb-locked');
    document.documentElement.removeAttribute('scroll-lock');
    if (document.body.style.top) {
      document.body.style.top = '';
      /* behavior:'instant' is load-bearing. The theme sets `scroll-behavior:
         smooth` on <html>, so a plain scrollTo ANIMATES this restore — and
         since dropping position:fixed has already dumped the document at 0,
         what the visitor sees is the page snapping to the top and then
         scrolling itself back down (owner 2026-08-04: "it jumps to the top of
         the page, and scrolls back down to where i was before"). 'instant'
         overrides the CSS for this one call, so the position is simply never
         lost on screen. */
      window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
    }
  }

  function syncAria() {
    for (const [name, sel] of Object.entries(TRIGGERS)) {
      document.querySelectorAll(sel).forEach((el) => {
        el.setAttribute('aria-expanded', String(overlay === name));
      });
    }
  }

  function setOverlay(next, trigger) {
    if (next === overlay) return;
    const opening = next !== 'none';

    if (opening && overlay === 'none') lastTrigger = trigger || document.activeElement;

    overlay = next;
    root.dataset.overlay = next;
    syncAria();

    if (opening && LOCKS.has(next)) lockScroll();
    else unlockScroll();

    root.dispatchEvent(new CustomEvent('sb:overlay', { detail: { overlay: next } }));

    if (!opening && lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus({ preventScroll: true });
      lastTrigger = null;
    }
  }

  const close = () => setOverlay('none');
  const toggle = (name, trigger) => setOverlay(overlay === name ? 'none' : name, trigger);

  /* ── Pill geometry ──────────────────────────────────────────────────
     The pill's height is owned by CSS (--sb-pill-h, 76 desktop / 66 mobile) and
     the measured value is published under a DIFFERENT name.

     It must never be written back into --sb-pill-h. That variable sets the
     height, so measuring the element and assigning the result to it closes a
     loop: an inline value written at one breakpoint outranks the stylesheet at
     every other one, and the pill stays whatever it happened to be when the
     script first ran. This cost an hour — the pill sat at the mobile 66px on a
     1440px viewport and no CSS rule looked wrong, because none of them was.

     The measured number is still worth having: the mobile shell grows out from
     the pill's true bottom edge, and that has to be a real pixel value. */
  const pill = root.querySelector('.sb-c-pill');
  let pillRaf = 0;

  function measurePill() {
    cancelAnimationFrame(pillRaf);
    pillRaf = requestAnimationFrame(() => {
      if (!pill) return;
      const h = Math.round(pill.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty('--sb-pill-measured', `${h}px`);
    });
  }

  measurePill();
  addEventListener('resize', measurePill, { passive: true });
  if (window.ResizeObserver && pill) new ResizeObserver(measurePill).observe(pill);

  /* ── Nav hover ──────────────────────────────────────────────────────
     One flag on the <nav> drives all three states: the hovered item darkens
     and scales, its siblings recede, and everything resets on leave. Done with
     a flag rather than :has() so it behaves identically everywhere.

     Weight never changes — that would reflow the row and shift the siblings,
     which is exactly what the absolute centring is there to prevent. */
  const nav = root.querySelector('[data-sb-nav]');
  const head = root.querySelector('.sb-c-head');

  if (nav) {
    nav.addEventListener('pointerenter', () => { nav.dataset.hovering = ''; }, { passive: true });
    nav.addEventListener('pointerleave', () => { delete nav.dataset.hovering; }, { passive: true });

    nav.querySelectorAll('.sb-c-nav__item').forEach((item) => {
      const isMega = item.hasAttribute('data-sb-mega-trigger');
      /* Hovering a non-mega item CLOSES the mega panel — the handoff is
         explicit that moving along the nav dismisses it. */
      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'touch') return;
        if (isMega) setOverlay('mega', item);
        else if (overlay === 'mega') close();
      }, { passive: true });

      if (isMega) {
        item.addEventListener('click', () => toggle('mega', item));
        item.addEventListener('focus', () => { if (overlay !== 'none' && overlay !== 'mega') close(); });
      }
    });
  }

  /* Leaving the whole header — pill AND the panel hanging off it — closes the
     mega menu. The wrapper's 18px top padding is what keeps the gap between
     them inside this element, so the pointer never leaves mid-journey. */
  if (head) {
    head.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'touch') return;
      if (overlay === 'mega') close();
      if (nav) delete nav.dataset.hovering;
    }, { passive: true });
  }

  /* ── Triggers ───────────────────────────────────────────────────────
     The mega trigger is bound above (it needs the hover pairing); the rest are
     plain toggles. Search/cart/menu panels arrive with their own modules and
     only need the state set here. */
  /* DOCUMENT-wide, unlike the other triggers. The footer carries a country
     button of its own on mobile and it lives outside the chrome, so a
     root-scoped query would never see it. Both buttons drive the one panel —
     there is a single country list in the DOM and a single code path, rather
     than a second copy of 29 rows in the footer. */
  document.querySelectorAll('[data-sb-country-trigger]').forEach((el) =>
    el.addEventListener('click', () => toggle('country', el)));

  /* ── Country list fades ─────────────────────────────────────────────
     Top fade only once scrolled off the start, bottom only while more remains
     — the same rule the horizontal rails follow, so a list that fits shows
     neither. Synced on open too, because the panel is measured while closed
     and scrollHeight is not known until it is laid out. */
  const countryPanel = root.querySelector('[data-sb-country-panel]');
  const countryForm = countryPanel?.querySelector('.sb-c-country-panel__form');
  /* TWO SCROLLERS, one rule. On desktop the FORM scrolls inside a fixed
     dropdown; on mobile the form is laid out full height and the shell's grow
     region scrolls instead. Whichever is actually scrolling gets measured, and
     the fade attributes land on the element that carries the fade — the panel
     on desktop, the grow region on mobile. */
  function syncCountryFades() {
    if (!countryPanel) return;
    /* Looked up here, not captured: `grow` is declared further down this file
       and referencing it at this point is a temporal-dead-zone ReferenceError.
       This function only ever runs from an event, long after that line. */
    const shell = root.querySelector('[data-sb-grow]');
    const onShell = !!shell && countryPanel.parentElement === shell;
    const scroller = onShell ? shell : countryForm;
    const target = onShell ? shell : countryPanel;
    if (!scroller || !target) return;
    const max = scroller.scrollHeight - scroller.clientHeight;
    target.toggleAttribute('data-fade-top', scroller.scrollTop > 2);
    target.toggleAttribute('data-fade-bottom', max > 2 && scroller.scrollTop < max - 2);
  }
  countryForm?.addEventListener('scroll', syncCountryFades, { passive: true });
  root.querySelector('[data-sb-grow]')?.addEventListener('scroll', syncCountryFades, { passive: true });
  root.addEventListener('sb:overlay', (e) => {
    if (e.detail.overlay === 'country') requestAnimationFrame(syncCountryFades);
  });
  root.querySelectorAll('[data-sb-search-trigger]').forEach((el) =>
    el.addEventListener('click', () => toggle('search', el)));
  root.querySelectorAll('[data-sb-cart-trigger]').forEach((el) =>
    el.addEventListener('click', () => toggle('cart', el)));
  /* The burger CLOSES the country list rather than swapping to the menu. On
     mobile that list opens from the footer, so the burger is the only close
     affordance in the header while it is up — and it is already showing an X by
     then (see the glyph rules in sb-chrome-styles). Sending it to the menu
     instead would contradict the icon the visitor is looking at. */
  root.querySelectorAll('[data-sb-menu-trigger]').forEach((el) =>
    el.addEventListener('click', () => {
      if (overlay === 'country') return setOverlay('none', el);
      toggle('menu', el);
    }));

  /* DELEGATED, not bound per element. The cart is rendered as its own section
     in <body> and only adopted into the chrome after this runs, so a direct
     binding never saw its X — and every Section Rendering refresh replaces the
     node, which would drop the listener again anyway. */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-sb-close]')) close();
  });

  /* ── Keyboard ───────────────────────────────────────────────────────
     Esc closes and returns focus to whatever opened the layer. Tab is trapped
     inside click-opened panels only — the mega menu is a hover surface that
     sits next in document order, so trapping it would strand keyboard users
     who simply tabbed past the nav. */
  addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay !== 'none') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab' || overlay === 'none' || overlay === 'mega') return;

    const panel = root.querySelector(`[data-sb-panel='${overlay}']`);
    if (!panel) return;

    const focusables = [...panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    )].filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* ── Mobile shell: measured growth ──────────────────────────────────
     The shell's height is a real pixel value, measured from the active level
     and written before the frame paints. Two things depend on getting this
     right and neither tolerates `auto`:

       · `height: auto` cannot be transitioned at all.
       · A `max-height` cap animates towards a ceiling the content does not
         reach, so the tail of the collapse is instant — the sheet snaps shut
         the moment the children unmount.

     `scrollHeight + 4` is the handoff's figure: each level carries 14px of
     bottom padding, so the +4 lands every level on an identical 18px gap
     regardless of how many rows it has. */
  const grow = root.querySelector('[data-sb-grow]');
  const levels = [...root.querySelectorAll('.sb-c-level')];
  let menuLevel = 0;
  let menuBranch = 'root';
  let measureRaf = 0;

  const activeLevel = () =>
    levels.find((l) => Number(l.dataset.menuLevel) === menuLevel && l.dataset.branch === menuBranch);

  function paintLevels() {
    const active = activeLevel();
    levels.forEach((l) => {
      const isActive = l === active;
      const depth = Number(l.dataset.menuLevel);
      if (isActive) l.dataset.state = 'active';
      else if (depth < menuLevel) l.dataset.state = 'behind';
      else delete l.dataset.state;
      /* Inert rather than hidden: a display:none level cannot be measured, and
         these are absolutely positioned so they cost no layout while inert. */
      l.inert = !isActive;
      l.setAttribute('aria-hidden', String(!isActive));
    });
  }

  /**
   * How much room the shell may take without reaching the bottom of the screen
   * or the home indicator. visualViewport, not innerHeight, because iOS changes
   * it as the URL bar animates.
   *
   * MEASURED FROM THE PILL'S OWN LIVE BOTTOM EDGE, not recomputed from
   * --sb-pill-inset + pill height. Those two used to be enough to know where
   * the pill sits, until the away notice started moving it down by an extra
   * --sb-away-shift on top of its own inset (snippets/sb-chrome-styles.liquid)
   * — at which point this recomputation quietly went stale by exactly that
   * amount, and every mobile sheet grew 14px past the bottom of the visible
   * screen while a notice was live (owner, 2026-08-19). getBoundingClientRect
   * already reflects wherever the pill actually is, however it got there, so
   * reading it directly can't drift from a CSS value again the same way.
   */
  function shellRoom() {
    const vh = window.visualViewport?.height || window.innerHeight;
    const styles = getComputedStyle(document.documentElement);
    const inset = parseFloat(styles.getPropertyValue('--sb-pill-inset')) || 14;
    const pillBottom = pill ? pill.getBoundingClientRect().bottom : inset * 2 + 66;
    return Math.max(200, vh - inset - pillBottom);
  }

  /**
   * ALL THREE shell surfaces are measured here, not just the menu. Search and
   * cart open into the same grow region, so a version of this that only knew
   * about the menu left them rendered, correctly styled, and clipped to zero
   * height — present in the DOM and invisible on screen.
   *
   * Each has its own idea of how tall it wants to be:
   *   menu   — the active level, scrollHeight + 4 (see the note on the +4)
   *   search — the panel's own laid-out height, which its results region has
   *            already sized in px
   *   cart   — a flat 690px content region, per the handoff
   * and all three are clamped to what actually fits on screen.
   */
  function measureShell() {
    if (!grow) return;
    cancelAnimationFrame(measureRaf);
    measureRaf = requestAnimationFrame(() => {
      if (!mobile()) { grow.style.height = ''; grow.removeAttribute('data-scroll'); return; }

      let wanted = 0;
      if (overlay === 'menu') {
        const el = activeLevel();
        wanted = el ? el.scrollHeight + 4 : 0;
      } else if (overlay === 'search') {
        const el = root.querySelector('[data-sb-search]');
        /* rect, not scrollHeight: the panel is laid out at its natural height
           even while the region clipping it is still at zero. */
        wanted = el ? Math.ceil(el.getBoundingClientRect().height) : 0;
      } else if (overlay === 'cart') {
        wanted = 690;
      } else if (overlay === 'country') {
        /* Owner 2026-08-05: the country list should extend the header the way
           the other menus do, rather than hanging under it as a floating panel.
           So it is a fourth shell surface on mobile.

           scrollHeight of the FORM plus the panel's own vertical padding: the
           form carries its own max-height for the desktop panel, which caps
           getBoundingClientRect well below the list's real length, and asking
           the panel for its rect would inherit that cap. shellRoom() below is
           what actually limits it here, and data-scroll then lets the list
           scroll inside the shell exactly as the menu does when it is long. */
        const panel = root.querySelector('[data-sb-country-panel]');
        const list = panel?.querySelector('.sb-c-country-panel__form');
        if (list) {
          const cs = getComputedStyle(panel);
          const pad = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
          wanted = Math.ceil(list.scrollHeight + pad);
        }
      }

      if (!wanted) { grow.style.height = '0px'; grow.removeAttribute('data-scroll'); return; }

      /* The country list stops at 80% of the room rather than filling it
         (owner 2026-08-05: "don't make it extend so far down the page"). A list
         of 29 near-identical rows reads as endless when it runs to the bottom of
         the screen, and leaving a strip of page visible keeps it feeling like a
         panel over the site rather than a takeover. The menu and cart still take
         the full room — they are destinations, this is a picker. */
      const room = overlay === 'country' ? Math.round(shellRoom() * 0.8) : shellRoom();
      const h = Math.min(wanted, room);
      /* Only let the region scroll when it is actually short of room, so the
         normal case keeps a clean clipped collapse with no scrollbar. */
      grow.toggleAttribute('data-scroll', wanted > room);
      grow.style.height = `${h}px`;
    });
  }

  function goTo(level, branch, focusIt) {
    menuLevel = level;
    menuBranch = branch;
    paintLevels();
    measureShell();
    if (focusIt) {
      const target = activeLevel()?.querySelector('.sb-c-back, .sb-c-row');
      target?.focus({ preventScroll: true });
    }
  }

  root.querySelectorAll('[data-sb-drill]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const branch = btn.dataset.sbDrill;
      goTo(branch.includes('-') ? 2 : 1, branch, true);
    });
  });

  root.querySelectorAll('[data-sb-back]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const to = btn.dataset.sbBack;
      goTo(to === '0' ? 0 : 1, to === '0' ? 'root' : to, true);
    });
  });

  /* Opening the menu always starts at the root, and closing resets it so the
     next open is not halfway down someone else's brand list. */
  root.addEventListener('sb:overlay', (e) => {
    /* The menu always reopens at the root, so the next open is not halfway down
       someone else's brand list. Every shell surface then re-measures — search
       and cart grow the same region the menu does. */
    if (e.detail.overlay === 'menu') goTo(0, 'root', false);
    else measureShell();
  });

  paintLevels();
  addEventListener('resize', measureShell, { passive: true });

  /* The search panel changes height on its own schedule — results arrive, the
     loading state swaps out, a thumb decodes — and each of those happens after
     whatever call last measured the shell. Relying on measuring at the right
     moment meant the sheet often opened to the height of the LOADING state and
     stayed there, cutting off the results underneath it. Watching the panel
     removes the timing question: whenever it changes, the shell follows.
     No feedback loop — the shell's height does not affect the panel's, which
     is laid out at its natural size inside a clipped region. */
  const searchPanel = root.querySelector('[data-sb-search]');
  if (window.ResizeObserver && searchPanel) {
    new ResizeObserver(() => { if (overlay === 'search') measureShell(); }).observe(searchPanel);
  }

  /* ── Visual viewport ────────────────────────────────────────────────
     100vh is wrong in iOS Safari while the URL bar animates, and the shell is
     capped against the viewport so it can never reach the home indicator.
     visualViewport is the only value that tracks the bar honestly. */
  const vv = window.visualViewport;
  if (vv) {
    const publishVV = () => {
      document.documentElement.style.setProperty('--sb-vvh', `${Math.round(vv.height)}px`);
      measureShell();
    };
    publishVV();
    vv.addEventListener('resize', publishVV, { passive: true });
  }

  /* ── Panel reparenting ──────────────────────────────────────────────
     The search (and later the cart) is a fixed panel on desktop and part of the
     shell on mobile. One element, moved between the two homes, rather than two
     copies of the markup that could drift apart. The move only happens when the
     breakpoint is actually crossed, so it costs nothing during normal use. */
  const panels = root.querySelector('[data-sb-panels]');
  const searchEl = root.querySelector('[data-sb-search]');

  function placePanels() {
    if (!panels || !grow) return;
    const wantMobile = mobile();
    const home = wantMobile ? grow : panels;

    if (searchEl && searchEl.parentElement !== home) {
      /* Preserve what the visitor typed across the move. */
      const field = searchEl.querySelector('[data-sb-search-input]');
      const value = field ? field.value : '';
      const hadFocus = document.activeElement === field;
      home.appendChild(searchEl);
      const moved = searchEl.querySelector('[data-sb-search-input]');
      if (moved) {
        moved.value = value;
        if (hadFocus) moved.focus({ preventScroll: true });
      }
    }

    /* The cart is rendered as its own section in <body> so it can be
       re-rendered independently; it is adopted into the chrome here. Looked up
       fresh each time because the Section Rendering API replaces the node. */
    const cartEl = document.querySelector('[data-sb-cart]');
    if (cartEl && cartEl.parentElement !== home) home.appendChild(cartEl);

    /* The country list joins them on mobile, so it extends the header instead of
       floating beneath it. Its DESKTOP home is the panel layer, where it is a
       fixed dropdown anchored to the flag button — moving it back on the way up
       is what keeps that working. */
    const countryEl = root.querySelector('[data-sb-country-panel]');
    if (countryEl && countryEl.parentElement !== home) home.appendChild(countryEl);
  }

  placePanels();
  MOBILE_Q.addEventListener('change', placePanels);

  /* ── Search ─────────────────────────────────────────────────────────
     Predictive Search, 460ms debounce, with the loading state as a real state
     rather than a flicker. Closing holds the last results for 400ms and fades
     them while the region animates between two MEASURED heights — the handoff
     is explicit that capping alone snaps when the children unmount. */
  const SEARCH_DEBOUNCE = 460;
  const SEARCH_HOLD = 400;
  const input = () => root.querySelector('[data-sb-search-input]');
  const region = () => root.querySelector('[data-sb-search-region]');
  const inner = () => root.querySelector('[data-sb-search-inner]');
  const clearBtn = () => root.querySelector('[data-sb-clear]');

  let searchTimer = 0;
  let holdTimer = 0;
  let inflight = null;
  let lastQuery = '';

  /* Predictive Search returns prices as DECIMAL STRINGS ("39.95"), not the
     integer cents the Cart API uses — dividing by 100 here renders £0.40 for a
     £40 part. Absent compare-at values come back as "0.00" rather than null, so
     a sale check has to test the number, not the truthiness of the string. */
  const money = (amount) =>
    new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'GBP',
    }).format(Number(amount) || 0);

  const svgChev = '<svg class="sb-c-result__chev" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>';
  const svgArrow = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>';

  /**
   * Height is set from the measured inner wrapper, never from `auto` — and
   * SYNCHRONOUSLY, in the same task that wrote the content.
   *
   * Doing it in a rAF left one frame where the region still held the previous
   * height while the new content was already in the DOM. Anything measuring the
   * panel in that frame — the shell on mobile, via its ResizeObserver — saw the
   * old height, grew to it, and then had to grow again once the real height
   * landed. That is the two-stage expansion: the sheet opened to roughly the
   * loading state's height, then jumped out to fit the results.
   *
   * Reading scrollHeight here forces a layout, which is the point: it is the
   * cost of having one correct value instead of two wrong ones.
   */
  function sizeRegion() {
    const reg = region();
    const inr = inner();
    if (!reg || !inr) return;
    reg.style.height = `${inr.scrollHeight}px`;
    measureShell();
  }

  function renderLoading() {
    const inr = inner();
    if (!inr) return;
    /* If the spinner is already up, LEAVE IT. Re-writing the markup on every
       keystroke rebuilt the element, so its rotation restarted from zero each
       time and the wheel appeared to snap backwards as you typed. The loading
       state is the same state whether it is the first character or the fifth. */
    if (inr.querySelector('.sb-c-spinner')) return;
    inr.innerHTML =
      '<div class="sb-c-search__loading"><div class="sb-c-spinner"></div>' +
      '<div class="sb-c-search__label">Searching</div></div>';
    sizeRegion();
  }

  function renderResults(query, data) {
    const inr = inner();
    if (!inr) return;
    /* SOLD OUT SINKS. Predictive Search ranks purely on relevance, so a sold-out
       part can take the top slot and the first thing a visitor sees is
       something they cannot buy — on a store whose whole job is "we have the
       one part you need", that is the worst possible answer.
       Available first, sold-out after, and relevance order preserved WITHIN
       each group because Array.sort is stable. Sorting before the slice is the
       point: an in-stock match ranked fifth should displace a sold-out one
       ranked second, which is why the fetch asks for more than it shows. */
    /* Six on desktop, four on mobile. The desktop panel hangs in free space and
       can afford the rows; the mobile sheet is capped against the viewport, so
       more rows there just push the "View all" link behind a scroll. */
    const maxRows = mobile() ? 4 : 6;
    const products = (data?.resources?.results?.products || [])
      .slice()
      .sort((a, b) => (a.available === b.available ? 0 : a.available ? -1 : 1))
      .slice(0, maxRows);
    /* Suggestion terms come from the store's own query suggestions. The
       prototype's "replacement <q>" heuristic is a placeholder and the handoff
       says explicitly not to port it. */
    const queries = (data?.resources?.results?.queries || []).slice(0, 6);

    let html = '';
    if (queries.length) {
      html += '<div class="sb-c-sugs"><div class="sb-c-sugs__scroll" data-noscrollbar data-sb-pill-scroll><div class="sb-c-sugs__track">';
      queries.forEach((qr, i) => {
        html += `<button class="sb-c-sug" type="button" data-sb-suggest="${escapeAttr(qr.text)}" style="animation-delay:${i * 50}ms">${escapeHtml(qr.text)}</button>`;
      });
      html += '</div></div><div class="sb-c-fade sb-c-fade--l" data-sb-fade-l></div><div class="sb-c-fade sb-c-fade--r" data-sb-fade-r></div></div>';
    }

    products.forEach((p, i) => {
      const soldOut = p.available === false;
      const price = Number(p.price_min ?? p.price) || 0;
      const compare = Number(p.compare_at_price_min ?? p.compare_at_price) || 0;
      const onSale = !soldOut && compare > price;
      const delay = (queries.length ? queries.length * 50 : 0) + i * 50;
      html +=
        `<a class="sb-c-result" href="${escapeAttr(p.url)}"${soldOut ? ' data-sold-out' : ''} style="animation-delay:${delay}ms">` +
        (p.featured_image?.url
          ? `<img class="sb-c-result__thumb" src="${escapeAttr(p.featured_image.url)}" alt="" width="46" height="46" loading="lazy">`
          : '<span class="sb-c-result__thumb"></span>') +
        `<span class="sb-c-result__main"><span class="sb-c-result__name">${escapeHtml(p.title)}</span>` +
        (soldOut ? '<span class="sb-c-tag" data-tag="sold-out">Sold Out</span>' : '') +
        '</span>' +
        (soldOut
          ? ''
          : '<span class="sb-c-result__prices">' +
            (onSale ? `<span class="sb-c-result__was">${money(compare)}</span>` : '') +
            `<span class="sb-c-result__now"${onSale ? ' data-sale' : ''}>${money(price)}</span></span>`) +
        svgChev +
        '</a>';
    });

    const total = data?.resources?.results?.products?.length ?? 0;
    if (products.length) {
      const delay = (queries.length * 50) + (products.length * 50);
      html +=
        `<a class="sb-c-arrow sb-c-search__all" href="${escapeAttr(routeSearch(query))}" style="animation-delay:${delay}ms">` +
        `<span>View all ${total} result${total === 1 ? '' : 's'}</span>${svgArrow}</a>`;
    } else {
      html += noMatchLink();
    }

    inr.innerHTML = html;
    sizeRegion();
    wirePillRail();
  }

  /* The empty result is a WAY OUT, not an apology — so it reads as the same
     kind of thing as "View all n results": accent, arrow, and the same gap
     growth on hover. Muted grey with no arrow made it look like a status
     message you could not act on (owner 2026-08-03). */
  const noMatchLink = () =>
    `<a class="sb-c-arrow sb-c-search__none" href="${escapeAttr(allProductsUrl)}">` +
    `<span>No matches — browse all</span>${svgArrow}</a>`;

  const allProductsUrl = root.dataset.allProducts || '/collections/all';
  const searchUrl = root.dataset.searchUrl || '/search';
  const routeSearch = (q) => `${searchUrl}?q=${encodeURIComponent(q)}`;

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
  const escapeAttr = escapeHtml;

  /** Edge fades: left only once scrolled off the start, right only while more
      remains. Seeded false/true so the resting state is already correct. */
  function wirePillRail() {
    const scroller = root.querySelector('[data-sb-pill-scroll]');
    const fl = root.querySelector('[data-sb-fade-l]');
    const fr = root.querySelector('[data-sb-fade-r]');
    if (!scroller || !fl || !fr) return;
    const sync = () => {
      const max = scroller.scrollWidth - scroller.clientWidth;
      fl.toggleAttribute('data-on', scroller.scrollLeft > 2);
      fr.toggleAttribute('data-on', scroller.scrollLeft < max - 2);
    };
    scroller.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  function closeResults() {
    /* Hold the outgoing content, fade it, and only then empty the region — so
       the collapse has something to collapse and does not jump. */
    const reg = region();
    const inr = inner();
    if (!reg || !inr) return;
    clearTimeout(holdTimer);
    reg.setAttribute('data-fading', '');
    reg.style.height = '0px';
    measureShell();
    holdTimer = setTimeout(() => {
      inr.innerHTML = '';
      reg.removeAttribute('data-fading');
    }, SEARCH_HOLD);
  }

  function runSearch(q) {
    inflight?.abort();
    inflight = new AbortController();
    /* Ask for 10 (the API's ceiling) and show 4. The extra six are the pool the
       sold-out sort draws from — with a limit of 6 a query matching mostly
       sold-out parts had nothing in-stock left to promote. */
    fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,query&resources[limit]=10`, {
      signal: inflight.signal,
      headers: { Accept: 'application/json' },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => { if (q === lastQuery) renderResults(q, data); })
      .catch((err) => {
        if (err.name === 'AbortError' || q !== lastQuery) return;
        const inr = inner();
        if (inr) {
          inr.innerHTML = noMatchLink();
          sizeRegion();
        }
      });
  }

  function onQuery() {
    const el = input();
    if (!el) return;
    const q = el.value.trim();
    lastQuery = q;
    searchEl.toggleAttribute('data-has-query', q.length > 0);
    clearBtn()?.toggleAttribute('hidden', q.length === 0);

    clearTimeout(searchTimer);
    if (!q) { inflight?.abort(); closeResults(); return; }

    renderLoading();
    searchTimer = setTimeout(() => runSearch(q), SEARCH_DEBOUNCE);
  }

  root.addEventListener('input', (e) => {
    if (e.target.matches('[data-sb-search-input]')) onQuery();
  });

  root.addEventListener('click', (e) => {
    const clear = e.target.closest('[data-sb-clear]');
    if (clear) {
      const el = input();
      if (el) { el.value = ''; el.focus({ preventScroll: true }); }
      onQuery();
      return;
    }
    /* Tapping a suggestion fills the field and skips the loading state — the
       term is already known, so a spinner would be theatre. */
    const sug = e.target.closest('[data-sb-suggest]');
    if (sug) {
      const el = input();
      if (!el) return;
      el.value = sug.dataset.sbSuggest;
      lastQuery = el.value.trim();
      searchEl.toggleAttribute('data-has-query', true);
      clearBtn()?.removeAttribute('hidden');
      clearTimeout(searchTimer);
      runSearch(lastQuery);
    }
  });

  root.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target.matches('[data-sb-search-input]')) {
      e.preventDefault();
      const q = e.target.value.trim();
      if (q) location.href = routeSearch(q);
    }
  });

  root.addEventListener('sb:overlay', (e) => {
    if (e.detail.overlay === 'search') {
      /* Focus the field EVERYWHERE, touch included, so the keyboard comes up
         with the panel (owner 2026-08-04: "when i open up the search on mobile
         make it so the keyboard immediately pops up and the search bar is
         focused"). This used to skip touch on the reasoning that the keyboard
         covers the results — but nobody opens search to read, they open it to
         type, and the extra tap was in the way.

         It works on iOS only because this whole path is synchronous inside the
         tap: click → toggle → setOverlay → this event → focus. Move any part of
         it behind a timeout, a rAF or an await and Safari will refuse to raise
         the keyboard, silently. */
      const el = input();
      if (el) {
        /* The panel is display:none while closed now (Safari 26 samples fixed
           boxes at the viewport edges — see snippets/sb-chrome-styles.liquid),
           so between setOverlay setting the attribute and this line the field
           may not have been rendered yet, and focus() on an unrendered element
           is a silent no-op. Reading offsetWidth forces the style and layout
           flush that makes it focusable. Deliberately a synchronous read and
           not a rAF: the whole point of the note above is that nothing on this
           path may be deferred, or iOS declines to raise the keyboard. */
        void el.offsetWidth;
        el.focus({ preventScroll: true });
      }
    } else {
      clearTimeout(searchTimer);
      inflight?.abort();
    }
  });

  /* ── Cart badge ─────────────────────────────────────────────────────
     The pop is a 260ms attribute rather than a keyframe, so a second add
     mid-pop restarts it instead of being swallowed by the running animation.
     Reduced motion drops the scale but keeps the count update. */
  const badge = root.querySelector('[data-sb-badge]');
  const live = root.querySelector('[data-sb-cart-count-live]');
  let popTimer = 0;

  function setCartCount(count, { pop = false } = {}) {
    if (badge) badge.textContent = String(count);
    if (live) live.textContent = `${count} ${count === 1 ? 'item' : 'items'} in cart`;
    if (!pop || !badge || reduced.matches) return;
    badge.removeAttribute('data-pop');
    void badge.offsetWidth; /* restart the transition */
    badge.setAttribute('data-pop', '');
    clearTimeout(popTimer);
    popTimer = setTimeout(() => badge.removeAttribute('data-pop'), 260);
  }

  /* Shopify's own cart events, so the badge tracks adds made anywhere on the
     page — product form, quick-add, the drawer's own cross-sell. */
  document.addEventListener('cart:update', (e) => {
    const c = e.detail?.resource ?? e.detail?.cart;
    if (c && typeof c.item_count === 'number') setCartCount(c.item_count, { pop: true });
  });

  /* ── Public surface for the panel modules ──────────────────────────── */
  window.SBChrome = {
    open: (name, trigger) => setOverlay(name, trigger),
    close,
    toggle,
    get overlay() { return overlay; },
    setCartCount,
    lockScroll,
    unlockScroll,
    measurePill,
    measureShell,
    placePanels,
    reduced: () => reduced.matches,
    isMobile: mobile,
    root,
  };
})();
